import { queryClient, trpcOptions } from '@client/trpc/client';
import {
    ASIC_PRODUCTS_FULL_TREE_INPUT,
    DETAIL_LOOKUP_PREFETCH_OPTIONS,
    DETAIL_RECORD_PREFETCH_OPTIONS,
    fieldConfigurationViewInput,
} from '@client/trpc/query-options';
import type { QueryKey } from '@tanstack/react-query';

/**
 * Fire-and-forget warm-up of the org-wide lookups the complaint manage tab's eager cards
 * render from — the COMPLAINT custom field configurations and the full ASIC product tree.
 * Otherwise they leave in a second batch after the record renders, and the cards show
 * skeleton bars and a raw ASIC code until it returns. Their observers use the short
 * `DETAIL_LOOKUP_QUERY_OPTIONS` window:
 * - Only when the target shows the manage tab (`showsManageTab`). `beforeLoad` reruns for the
 *   retained details match on every in-page navigation, and the other tabs render neither lookup.
 * - Skipped per lookup while the manage tab is already mounted for this complaint (the lookup
 *   and this complaint's record are both observed), e.g. opening or closing the note and
 *   link-incident drawers: the mounted observers own the lookups' freshness, and a refetch
 *   would resend the full ASIC tree. Another complaint is unobserved and still prefetches.
 * - A navigation prefetches with that window, so a lookup the observer would refetch on mount
 *   is refreshed here and the observer joins the in-flight request.
 * - A hover-intent preload keeps the 30 s detail-record window: the click reruns `beforeLoad`
 *   as a navigation, which refreshes anything older than the window.
 */
export function prefetchComplaintManageLookups(
    id: bigint,
    { preload, showsManageTab }: { preload: boolean; showsManageTab: boolean },
): void {
    if (!showsManageTab) {
        return;
    }
    // A mounted page observing this record means it is this complaint's own (prefetches add no
    // observers, so the caller's record prefetch may run first).
    const isComplaintShown = isObserved(trpcOptions.complaint.getComplaint.queryKey({ id }));
    const lookupOptions = preload ? DETAIL_RECORD_PREFETCH_OPTIONS : DETAIL_LOOKUP_PREFETCH_OPTIONS;
    const isOwnedByMountedTab = (queryKey: QueryKey) => isComplaintShown && isObserved(queryKey);
    const fieldConfigurations = trpcOptions.fieldConfiguration.listFieldConfigurations.queryOptions(
        fieldConfigurationViewInput('COMPLAINT'),
        lookupOptions,
    );
    if (!isOwnedByMountedTab(fieldConfigurations.queryKey)) {
        void queryClient.prefetchQuery(fieldConfigurations);
    }
    const asicProducts = trpcOptions.asicProduct.listAsicProducts.queryOptions(
        ASIC_PRODUCTS_FULL_TREE_INPUT,
        lookupOptions,
    );
    if (!isOwnedByMountedTab(asicProducts.queryKey)) {
        void queryClient.prefetchQuery(asicProducts);
    }
}

function isObserved(queryKey: QueryKey): boolean {
    return (queryClient.getQueryCache().find({ queryKey, exact: true })?.getObserversCount() ?? 0) > 0;
}
